import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Calendar, CheckCircle, Clock, DollarSign, X, User, Star, ChevronDown, ChevronUp, Repeat } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import BookingDialog from "../booking/BookingDialog";

interface Application {
  id: string;
  application_id?: string;
  status: string;
  created_at: string;
  message: string;
  remaining_sessions: number | null;
  subscription_end_date?: string;
  // Session booking data for completed applications
  booked_date?: string;
  start_time?: string;
  end_time?: string;
  booking_id?: string;
  courses: {
    id: string;
    title: string;
    price: number;
    course_type: string;
    included_sessions: number | null;
  };
  profiles: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string;
  };
  review?: {
    rating: number;
    review_text: string | null;
    created_at: string;
  } | null;
  hasReview?: boolean;
  isSubscriptionCard?: boolean;
  subscription_id?: string;
  subscription_application_id?: string;
  // Subscription period data for slot cards
  period_start_date?: string;
  period_end_date?: string;
  total_allowance?: number;
  bookings_used?: number;
}

interface GroupedCompletedApplications {
  [mentorId: string]: {
    mentor: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url: string;
    };
    applications: Application[];
  };
}

const MentorApplications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [subscriptionBookingCards, setSubscriptionBookingCards] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsedMentors, setCollapsedMentors] = useState<Record<string, boolean>>({});
  const [bookingDialog, setBookingDialog] = useState<{
    open: boolean;
    mentorId: string;
    applicationId: string;
    mentorName: string;
    courseTitle: string;
  }>({
    open: false,
    mentorId: '',
    applicationId: '',
    mentorName: '',
    courseTitle: ''
  });

  useEffect(() => {
    if (user) {
      fetchApplications();
      fetchSubscriptionBookings();
    }
  }, [user]);

  // Add event listeners for real-time updates
  useEffect(() => {
    const handleSubscriptionBookingUpdate = (event: CustomEvent) => {
      console.log('DEBUG: Mentee - Received subscription-booking-updated event:', event.detail);
      if (event.detail.menteeId === user?.id) {
        console.log('DEBUG: Mentee - Refreshing subscription bookings due to update');
        fetchSubscriptionBookings();
      }
    };

    const handleBookingCompleted = (event: CustomEvent) => {
      console.log('DEBUG: Mentee - Received booking-completed event:', event.detail);
      // Refresh both regular applications and subscription bookings
      fetchApplications();
      fetchSubscriptionBookings();
    };

    const handleSubscriptionEnded = (event: CustomEvent) => {
      console.log('DEBUG: Mentee - Received subscription-ended event:', event.detail);
      if (event.detail.menteeId === user?.id) {
        console.log('DEBUG: Mentee - Refreshing applications due to subscription end');
        // Refresh both regular applications and subscription bookings
        fetchApplications();
        fetchSubscriptionBookings();
      }
    };

    const handleReviewSubmitted = (event: CustomEvent) => {
      console.log('DEBUG: Mentee - Received review-submitted event:', event.detail);
      if (event.detail.reviewerId === user?.id) {
        console.log('DEBUG: Mentee - Refreshing applications due to review submission');
        // Add a longer delay to ensure the database has processed the review
        setTimeout(() => {
          console.log('DEBUG: Mentee - Delayed refresh of applications after review submission');
          fetchApplications();
        }, 2500);
      }
    };

    const handleBookingChangeRequested = (event: CustomEvent) => {
      console.log('DEBUG: Mentee - Received booking-change-requested event:', event.detail);
      if (event.detail.menteeId === user?.id) {
        console.log('DEBUG: Mentee - Refreshing due to booking change request');
        fetchApplications();
        fetchSubscriptionBookings();
      }
    };

    window.addEventListener('subscription-booking-updated', handleSubscriptionBookingUpdate as EventListener);
    window.addEventListener('booking-completed', handleBookingCompleted as EventListener);
    window.addEventListener('subscription-ended', handleSubscriptionEnded as EventListener);
    window.addEventListener('review-submitted', handleReviewSubmitted as EventListener);
    window.addEventListener('booking-change-requested', handleBookingChangeRequested as EventListener);

    return () => {
      window.removeEventListener('subscription-booking-updated', handleSubscriptionBookingUpdate as EventListener);
      window.removeEventListener('booking-completed', handleBookingCompleted as EventListener);
      window.removeEventListener('subscription-ended', handleSubscriptionEnded as EventListener);
      window.removeEventListener('review-submitted', handleReviewSubmitted as EventListener);
      window.removeEventListener('booking-change-requested', handleBookingChangeRequested as EventListener);
    };
  }, [user?.id]);

  // Initialize all mentor groups as collapsed when completed applications are loaded
  useEffect(() => {
    const groupedData = groupCompletedApplicationsByMentor();
    const allMentorIds = Object.keys(groupedData);
    if (allMentorIds.length > 0) {
      const initialCollapsedState = allMentorIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
      setCollapsedMentors(initialCollapsedState);
    }
  }, [applications]);

  const fetchApplications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      const { data: applicationsData, error } = await supabase
        .from('applications')
        .select(`
          id,
          application_id,
          status,
          created_at,
          message,
          remaining_sessions,
          course_id,
          mentor_id
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!applicationsData) {
        setApplications([]);
        return;
      }

      // Fetch course and mentor details separately
      const enrichedApplications = await Promise.all(
        applicationsData.map(async (app) => {
          // Fetch course details
          const { data: courseData } = await supabase
            .from('courses')
            .select('id, title, price, course_type, included_sessions')
            .eq('id', app.course_id)
            .single();

          // Fetch mentor details
          const { data: mentorData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, avatar_url')
            .eq('id', app.mentor_id)
            .single();

          // For completed applications, check if review exists and fetch review data
          let reviewData = null;
          let hasReview = false;
          let sessionBookingData = null; // Declare booking data variable
          
          console.log(`DEBUG: Checking reviews for app ${app.id}, status: ${app.status}, course type: ${courseData?.course_type}`);
          
          // Check for reviews regardless of status - sessions may be completed but status not updated
          if (courseData?.course_type === 'subscription') {
            // For subscription applications, check subscription_reviews table
            const { data: review, error: reviewError } = await supabase
              .from('subscription_reviews')
              .select('rating, review_text, created_at')
              .eq('application_id', app.application_id)
              .eq('reviewer_id', user.id)
              .eq('review_type', 'mentee_to_mentor')
              .maybeSingle();

            if (reviewError) {
              console.error(`DEBUG: Error fetching subscription review for app ${app.id}:`, reviewError);
            }

            reviewData = review;
            hasReview = !!review;
            console.log(`DEBUG: Subscription app ${app.id} hasReview: ${hasReview}, review:`, review);
          } else {
            // For session applications, check appointment_reviews table via session_bookings
            const { data: bookingData, error: bookingError } = await supabase
              .from('session_bookings')
              .select('id, status, booked_date, start_time, end_time, booking_id')
              .eq('application_id', app.id)
              .maybeSingle();

            console.log(`DEBUG: Session booking lookup for app ${app.id}:`, { bookingData, bookingError });

            if (bookingError) {
              console.error(`DEBUG: Error fetching session booking for app ${app.id}:`, bookingError);
            }

            if (bookingData) {
              sessionBookingData = bookingData; // Store booking data for later use
              const { data: review, error: reviewError } = await supabase
                .from('appointment_reviews')
                .select('rating, review_text, created_at')
                .eq('booking_id', bookingData.id)
                .eq('reviewer_id', user.id)
                .maybeSingle();

              console.log(`DEBUG: Session review lookup for booking ${bookingData.id}:`, { review, reviewError });

              if (reviewError) {
                console.error(`DEBUG: Error fetching session review for booking ${bookingData.id}:`, reviewError);
              }

              reviewData = review;
              hasReview = !!review;
              
              // For sessions, only show as completed if booking is completed AND has review
              if (bookingData.status === 'completed' && hasReview) {
                app.status = 'completed'; // Update status for display purposes
              }
              
              console.log(`DEBUG: Session app ${app.id} booking status: ${bookingData.status}, hasReview: ${hasReview}, final app status: ${app.status}`);
            } else {
              console.log(`DEBUG: No session booking found for app ${app.id}`);
            }
          }

          return {
            ...app,
            courses: courseData,
            profiles: mentorData,
            review: reviewData,
            hasReview,
            // Include session booking data if available
            ...(sessionBookingData && {
              booked_date: sessionBookingData.booked_date,
              start_time: sessionBookingData.start_time,
              end_time: sessionBookingData.end_time,
              booking_id: sessionBookingData.booking_id
            })
          };
        })
      );

      // Also fetch ended subscription applications that HAVE reviews for the completed tab
      const { data: endedSubscriptions, error: endedError } = await supabase
        .from('applications')
        .select(`
          id,
          application_id,
          status,
          created_at,
          message,
          subscription_end_date,
          mentor_id,
          course_id,
          user_id,
          payment_status,
          stripe_session_id,
          courses!inner (
            id,
            title,
            price,
            course_type
          )
        `)
        .eq('user_id', user.id)
        .eq('is_subscription_active', false)
        .not('subscription_end_date', 'is', null)
        .eq('courses.course_type', 'subscription')
        .order('subscription_end_date', { ascending: false });

      console.log('DEBUG: MentorApplications - Found ended subscription applications:', endedSubscriptions);

      // Process ended subscriptions with reviews
      const endedSubscriptionsWithReviews = [];
      if (!endedError && endedSubscriptions) {
        for (const app of endedSubscriptions) {
          // Fetch mentor profile
          const { data: mentorData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, avatar_url')
            .eq('id', app.mentor_id)
            .single();

          // Check if review exists for this subscription
          const { data: existingReview } = await supabase
            .from('subscription_reviews')
            .select('rating, review_text, created_at')
            .eq('reviewer_id', user.id)
            .eq('reviewee_id', app.mentor_id)
            .eq('application_id', app.application_id)
            .eq('review_type', 'mentee_to_mentor')
            .single();

          const hasReview = !!existingReview;
          console.log('DEBUG: MentorApplications - Ended subscription', app.id, 'has review:', hasReview);
          
          // Only include subscriptions WITH reviews (opposite of CompletedAppointments)
          if (hasReview && app.courses && mentorData) {
            endedSubscriptionsWithReviews.push({
              ...app,
              status: 'completed', // Set status to completed for display
              courses: app.courses,
              profiles: mentorData,
              review: existingReview,
              hasReview: true
            });
          }
        }
      }

      // Combine regular applications with ended subscriptions that have reviews
      // Filter out duplicates from endedSubscriptionsWithReviews that already exist in enrichedApplications
      const filteredEnrichedApplications = enrichedApplications.filter(app => app.courses && app.profiles);
      const existingApplicationIds = new Set(filteredEnrichedApplications.map(app => app.id));
      const uniqueEndedSubscriptions = endedSubscriptionsWithReviews.filter(app => !existingApplicationIds.has(app.id));
      
      const allApplications = [
        ...filteredEnrichedApplications,
        ...uniqueEndedSubscriptions
      ];

      setApplications(allApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionBookings = async () => {
    if (!user?.id) return;

    try {
      console.log("DEBUG: Mentee - Starting fetchSubscriptionBookings for user:", user.id);
      
      // Simplified approach: Get ALL subscription bookings for this user that have available slots
      const { data: subscriptionBookings, error } = await supabase
        .from('subscription_bookings')
        .select(`
          id,
          application_id,
          mentor_id,
          mentee_id,
          total_allowance,
          bookings_used,
          week_start_date,
          period_end_date,
          is_active
        `)
        .eq('mentee_id', user.id)
        .eq('is_active', true);

      console.log("DEBUG: Mentee - All subscription bookings query result:", { subscriptionBookings, error });

      if (error) {
        console.error("Mentee - Error fetching subscription bookings:", error);
        setSubscriptionBookingCards([]);
        return;
      }

      if (!subscriptionBookings || subscriptionBookings.length === 0) {
        console.log("DEBUG: Mentee - No subscription bookings found at all");
        setSubscriptionBookingCards([]);
        return;
      }

      // Filter for bookings with available slots and active subscriptions
      const activeBookings = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      for (const booking of subscriptionBookings) {
        const availableSlots = booking.total_allowance - booking.bookings_used;
        console.log(`DEBUG: Mentee - Checking booking ${booking.id} - Total: ${booking.total_allowance}, Used: ${booking.bookings_used}, Available: ${availableSlots}`);
        
        if (availableSlots <= 0) {
          console.log(`DEBUG: Mentee - Skipping booking ${booking.id} - no available slots`);
          continue;
        }

        // Check if period hasn't ended
        if (booking.period_end_date) {
          const periodEnd = new Date(booking.period_end_date);
          periodEnd.setHours(23, 59, 59, 999);
          if (periodEnd < today) {
            console.log(`DEBUG: Mentee - Skipping booking ${booking.id} - period has ended (${booking.period_end_date})`);
            continue;
          }
        }
        
        // Check if the related application/subscription is still active
        const { data: applicationData, error: appError } = await supabase
          .from('applications')
          .select('is_subscription_active, subscription_end_date')
          .eq('id', booking.application_id)
          .single();
          
        if (appError) {
          console.error(`DEBUG: Mentee - Error fetching application ${booking.application_id}:`, appError);
          continue;
        }
        
        if (!applicationData?.is_subscription_active) {
          console.log(`DEBUG: Mentee - Skipping booking ${booking.id} - subscription not active`);
          continue;
        }
        
        // Check if subscription hasn't ended
        if (applicationData.subscription_end_date && new Date(applicationData.subscription_end_date) <= new Date()) {
          console.log(`DEBUG: Mentee - Skipping booking ${booking.id} - subscription has ended`);
          continue;
        }
        
        console.log(`DEBUG: Mentee - Adding active booking ${booking.id} with ${availableSlots} available slots`);
        activeBookings.push(booking);
      }
      
      console.log("DEBUG: Mentee - Active bookings with available slots:", activeBookings);
      
      if (activeBookings.length > 0) {
        await processSubscriptionBookings(activeBookings);
      } else {
        console.log("DEBUG: Mentee - No active bookings with available slots found");
        setSubscriptionBookingCards([]);
      }
    } catch (error) {
      console.error("Exception in fetchSubscriptionBookings:", error);
      setSubscriptionBookingCards([]);
    }
  };

  const processSubscriptionBookings = async (subscriptionBookings: any[]) => {
    console.log("DEBUG: Mentee - Processing", subscriptionBookings.length, "subscription booking records");

    const bookingCards: Application[] = [];
    
    for (const booking of subscriptionBookings) {
      const availableSlots = booking.total_allowance - booking.bookings_used;
      console.log(`DEBUG: Mentee - Processing booking ${booking.id} - Available slots: ${availableSlots}`);
      
      // Fetch course and mentor details
      const { data: applicationData, error: appError } = await supabase
        .from('applications')
        .select(`
          course_id,
          courses:course_id (
            id,
            title,
            price,
            course_type,
            subscription_type
          )
        `)
        .eq('id', booking.application_id)
        .single();

      if (appError) {
        console.error(`DEBUG: Mentee - Error fetching application ${booking.application_id}:`, appError);
        continue;
      }

      // Fetch mentor profile
      const { data: mentorData, error: mentorError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url')
        .eq('id', booking.mentor_id)
        .single();

      if (mentorError) {
        console.error(`DEBUG: Mentee - Error fetching mentor ${booking.mentor_id}:`, mentorError);
        continue;
      }

      console.log(`DEBUG: Mentee - Successfully fetched data for booking ${booking.id}`);

      if (applicationData?.courses && mentorData) {
        // ✅ CREATE MULTIPLE BOOKING CARDS FOR EACH AVAILABLE SLOT
        for (let slotIndex = 0; slotIndex < availableSlots; slotIndex++) {
          const cardId = `subscription-${booking.id}-slot-${slotIndex + 1}`;
          console.log(`DEBUG: Mentee - Creating booking card ${cardId} (slot ${slotIndex + 1} of ${availableSlots}) for period ${booking.week_start_date} to ${booking.period_end_date}`);
          
          const bookingCard: Application = {
            id: cardId,
            status: 'bookingRequired',
            created_at: booking.week_start_date, // Use period start date
            message: `${applicationData.courses.subscription_type === 'monthly' ? 'Monthly' : 'Weekly'} subscription session ${slotIndex + 1} - Ready to book`,
            remaining_sessions: null,
            courses: {
              id: applicationData.courses.id,
              title: applicationData.courses.title,
              price: applicationData.courses.price,
              course_type: applicationData.courses.course_type,
              included_sessions: null,
            },
            profiles: {
              id: mentorData.id,
              first_name: mentorData.first_name,
              last_name: mentorData.last_name,
              avatar_url: mentorData.avatar_url,
            },
            isSubscriptionCard: true,
            subscription_id: booking.id,
            subscription_application_id: booking.application_id,
            // Add period data
            period_start_date: booking.week_start_date,
            period_end_date: booking.period_end_date,
            total_allowance: booking.total_allowance,
            bookings_used: booking.bookings_used,
          };

          bookingCards.push(bookingCard);
        }
      }
    }

    console.log(`DEBUG: Mentee - Generated ${bookingCards.length} total booking cards from ${subscriptionBookings.length} subscription records`);
    console.log("DEBUG: Mentee - Booking cards details:", bookingCards.map(card => ({
      id: card.id,
      mentor: `${card.profiles.first_name} ${card.profiles.last_name}`,
      course: card.courses.title,
      message: card.message
    })));
    
    setSubscriptionBookingCards(bookingCards);
  };

  const getStatusColor = (status: string, isSubscription = false) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'pending_mentee_purchase': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'bookingRequired': return 'bg-purple-100 text-purple-800';
      case 'completed': return isSubscription ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string, isSubscription = false) => {
    switch (status) {
      case 'pending': return 'Pending Approval';
      case 'pending_mentee_purchase': return 'Awaiting Payment';
      case 'paid': return 'Paid - Session Booked';
      case 'bookingRequired': return 'Ready to Book';
      case 'completed': return isSubscription ? 'Subscription Completed' : 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const handleBookSession = (application: Application) => {
    setBookingDialog({
      open: true,
      mentorId: application.profiles.id,
      applicationId: application.isSubscriptionCard ? application.subscription_application_id! : application.id,
      mentorName: `${application.profiles.first_name} ${application.profiles.last_name}`,
      courseTitle: application.courses.title
    });
  };

  const handleMakePayment = (applicationId: string) => {
    navigate(`/payment/${applicationId}`);
  };

  const filterApplicationsByStatus = (status: string) => {
    if (status === 'completed') {
      // Return completed applications that have reviews
      return applications.filter(app => app.status === status && app.hasReview);
    }
    
    if (status === 'bookingRequired') {
      // Only include non-subscription applications from the applications table + subscription slot cards
      // This prevents showing the same subscription both as an application and as slot cards
      const regularApps = applications.filter(app => 
        app.status === status && app.courses?.course_type !== 'subscription'
      );
      return [...regularApps, ...subscriptionBookingCards];
    }
    
    return applications.filter(app => app.status === status);
  };

  const getTabCount = (status: string) => {
    return filterApplicationsByStatus(status).length;
  };

  const groupCompletedApplicationsByMentor = (): GroupedCompletedApplications => {
    const completedApplications = filterApplicationsByStatus('completed');
    const grouped: GroupedCompletedApplications = {};

    completedApplications.forEach(app => {
      const mentorId = app.profiles.id;
      if (!grouped[mentorId]) {
        grouped[mentorId] = {
          mentor: {
            id: app.profiles.id,
            first_name: app.profiles.first_name,
            last_name: app.profiles.last_name,
            avatar_url: app.profiles.avatar_url
          },
          applications: []
        };
      }
      grouped[mentorId].applications.push(app);
    });

    return grouped;
  };

  const renderRegularApplicationCard = (application: Application) => (
    <Card key={application.id} className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold">{application.courses.title}</h3>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            {/* Show period info for subscription slot cards */}
            {application.isSubscriptionCard && application.period_start_date && application.period_end_date ? (
              <>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Period: {new Date(application.period_start_date).toLocaleDateString()} – {new Date(application.period_end_date).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {(application.total_allowance || 0) - (application.bookings_used || 0)} slots remaining
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  {application.courses.price}
                </span>
              </>
            ) : application.status === 'completed' && application.booked_date && application.start_time && application.end_time ? (
              <>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Session: {new Date(application.booked_date).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {application.start_time} - {application.end_time}
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  {application.courses.price}
                </span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Applied {new Date(application.created_at).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  {application.courses.price}
                </span>
              </>
            )}
            {application.courses.course_type === 'subscription' && application.courses.included_sessions && !application.isSubscriptionCard && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {application.remaining_sessions || application.courses.included_sessions} sessions remaining
              </span>
            )}
          </div>
          
          {application.profiles && (
            <p className="text-xs text-gray-500 mt-2">Mentor: {application.profiles.first_name} {application.profiles.last_name}</p>
          )}
          {application.status === 'completed' && application.application_id && (
            <p className="text-xs text-gray-500 mt-2">App ID: {application.application_id}</p>
          )}

          {/* Only show message for non-completed applications and non-subscription slot cards */}
          {application.message && application.status !== 'completed' && !application.isSubscriptionCard && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{application.message}</p>
            </div>
          )}

          {/* Display review for completed applications */}
          {application.status === 'completed' && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">Your Review</span>
              </div>
              {application.review ? (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < application.review!.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-gray-600 ml-2">
                      {application.review.rating}/5 stars
                    </span>
                  </div>
                  {application.review.review_text && (
                    <p className="text-sm text-gray-700 italic">
                      "{application.review.review_text}"
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Submitted on {new Date(application.review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No review submitted</p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2 mt-4">
            {application.status === 'pending_mentee_purchase' && (
              <Button 
                onClick={() => handleMakePayment(application.id)}
                className="bg-emu-blue hover:bg-emu-blue/90"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Make Payment
              </Button>
            )}
            
            {application.status === 'bookingRequired' && (
              <Button 
                onClick={() => handleBookSession(application)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Book Session
              </Button>
            )}
            
            {application.status === 'paid' && (
              <div className="flex items-center text-green-600">
                <CheckCircle className="h-4 w-4 mr-2" />
                Session Scheduled
              </div>
            )}
            
            {application.status === 'completed' && (
              <div className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 mr-2" />
                Session Completed
              </div>
            )}
            
            {application.status === 'rejected' && (
              <div className="flex items-center text-red-600">
                <X className="h-4 w-4 mr-2" />
                Rejected
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end">
          <Badge className={getStatusColor(application.status)}>
            {application.status === 'completed' && application.booked_date ? 'Session Completed' : getStatusText(application.status)}
          </Badge>
        </div>
      </div>
    </Card>
  );

  const renderSubscriptionCard = (application: Application) => (
    <Card key={`subscription-${application.id}`} className="bg-white border border-purple-200">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="font-semibold text-lg text-gray-900 mb-2">
              {application.courses.title}
            </h4>
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <Repeat className="h-4 w-4" />
                Subscription
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Ended {new Date(application.subscription_end_date || application.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                {application.courses.price}
              </span>
            </div>
            {application.profiles && (
              <p className="text-xs text-gray-500 mb-2">Mentor: {application.profiles.first_name} {application.profiles.last_name}</p>
            )}
            {application.application_id && (
              <p className="text-xs text-gray-500 mb-2">App ID: {application.application_id}</p>
            )}
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Subscription Completed
          </span>
        </div>

        {/* Display review for completed subscriptions */}
        {application.review && (
          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-emu-blue">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Your Review</h4>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-4 w-4 ${
                      star <= application.review!.rating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
            {application.review.review_text && (
              <p className="text-sm text-gray-700 mb-2 italic leading-relaxed">
                "{application.review.review_text}"
              </p>
            )}
            <p className="text-xs text-gray-500">
              Submitted on {new Date(application.review.created_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </Card>
  );

  const renderApplicationCard = (application: Application) => {
    // Debug logging to understand the data structure
    console.log('🔍 DEBUG renderApplicationCard - Application data:', application);
    console.log('🔍 DEBUG subscription_end_date:', application.subscription_end_date);
    console.log('🔍 DEBUG course_type:', application.courses?.course_type);
    console.log('🔍 DEBUG status:', application.status);
    
    const isSubscription = !!(application.subscription_end_date || (application.courses?.course_type === 'subscription' && application.status === 'completed'));
    
    console.log('🔍 DEBUG isSubscription result:', isSubscription);
    console.log('🔍 DEBUG Will use:', isSubscription ? 'renderSubscriptionCard' : 'renderRegularApplicationCard');
    
    if (isSubscription) {
      return renderSubscriptionCard(application);
    } else {
      return renderRegularApplicationCard(application);
    }
  };

  const renderCompletedSessionCard = (application: Application) => (
    <Card key={application.id} className="p-4 bg-gray-50 border-l-4 border-blue-200">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">{application.courses.title}</h4>
          <Badge className={getStatusColor(application.status)}>
            {application.status === 'completed' && application.booked_date ? 'Session Completed' : getStatusText(application.status)}
          </Badge>
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Completed {new Date(application.created_at).toLocaleDateString()}
          </span>
          <span className="flex items-center gap-1">
            <DollarSign className="h-4 w-4" />
            ${application.courses.price}
          </span>
        </div>
        {application.application_id && (
          <p className="text-xs text-gray-500">App ID: {application.application_id}</p>
        )}

        {/* Review section */}
        <div className="mt-3 p-3 bg-white rounded-lg border">
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-sm text-gray-800">Your Review</span>
          </div>
          {application.review ? (
            <div>
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3 w-3 ${
                      i < application.review!.rating
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="text-xs text-gray-600 ml-2">
                  {application.review.rating}/5 stars
                </span>
              </div>
              {application.review.review_text && (
                <p className="text-sm text-gray-700 italic mb-1">
                  "{application.review.review_text}"
                </p>
              )}
              <p className="text-xs text-gray-500">
                Reviewed on {new Date(application.review.created_at).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No review submitted</p>
          )}
        </div>
      </div>
    </Card>
  );

  const toggleMentorCollapse = (mentorId: string) => {
    setCollapsedMentors(prev => ({
      ...prev,
      [mentorId]: !prev[mentorId]
    }));
  };

  const collapseAll = () => {
    const groupedData = groupCompletedApplicationsByMentor();
    const allMentorIds = Object.keys(groupedData);
    const collapsed = allMentorIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
    setCollapsedMentors(collapsed);
  };

  const expandAll = () => {
    setCollapsedMentors({});
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">My Applications</h2>
      
      {applications.length === 0 && subscriptionBookingCards.length === 0 ? (
        <Card className="p-8 text-center">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Yet</h3>
          <p className="text-gray-500 mb-4">You haven't applied to any mentorship programs yet.</p>
          <Button onClick={() => navigate('/search-mentors')}>
            Find Mentors
          </Button>
        </Card>
      ) : (
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="pending" className="text-sm">
              Pending Approval ({getTabCount('pending')})
            </TabsTrigger>
            <TabsTrigger value="awaiting-payment" className="text-sm">
              Awaiting Payment ({getTabCount('pending_mentee_purchase')})
            </TabsTrigger>
            <TabsTrigger value="booking-required" className="text-sm">
              Booking Required ({getTabCount('bookingRequired')})
            </TabsTrigger>
            <TabsTrigger value="completed" className="text-sm">
              Completed ({getTabCount('completed')})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pending" className="space-y-4 mt-6">
            {filterApplicationsByStatus('pending').length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No pending applications</p>
              </Card>
            ) : (
              filterApplicationsByStatus('pending').map(renderApplicationCard)
            )}
          </TabsContent>
          
          <TabsContent value="awaiting-payment" className="space-y-4 mt-6">
            {filterApplicationsByStatus('pending_mentee_purchase').length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No applications awaiting payment</p>
              </Card>
            ) : (
              filterApplicationsByStatus('pending_mentee_purchase').map(renderApplicationCard)
            )}
          </TabsContent>
          
          <TabsContent value="booking-required" className="space-y-4 mt-6">
            {filterApplicationsByStatus('bookingRequired').length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No applications requiring booking</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {filterApplicationsByStatus('bookingRequired').map(renderApplicationCard)}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="completed" className="space-y-6 mt-6">
            {Object.keys(groupCompletedApplicationsByMentor()).length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No completed applications</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Collapse All / Expand All Controls */}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={collapseAll}
                    className="text-xs"
                  >
                    Collapse All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={expandAll}
                    className="text-xs"
                  >
                    Expand All
                  </Button>
                </div>

                {Object.entries(groupCompletedApplicationsByMentor()).map(([mentorId, group]) => (
                  <Collapsible
                    key={mentorId}
                    open={!collapsedMentors[mentorId]}
                    onOpenChange={() => toggleMentorCollapse(mentorId)}
                  >
                    <div className="space-y-4">
                      {/* Mentor Header - Clickable */}
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors cursor-pointer">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={group.mentor.avatar_url} />
                            <AvatarFallback>
                              {group.mentor.first_name[0]}{group.mentor.last_name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {group.mentor.first_name} {group.mentor.last_name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {group.applications.length} completed session{group.applications.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="flex items-center">
                            {collapsedMentors[mentorId] ? (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      {/* Sessions under this mentor - Collapsible Content */}
                      <CollapsibleContent className="space-y-3 ml-4 animate-accordion-down data-[state=closed]:animate-accordion-up">
                        {group.applications.map(renderApplicationCard)}
                      </CollapsibleContent>
                      
                      <Separator className="my-6" />
                    </div>
                  </Collapsible>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <BookingDialog
        open={bookingDialog.open}
        onOpenChange={(open) => setBookingDialog(prev => ({ ...prev, open }))}
        mentorId={bookingDialog.mentorId}
        applicationId={bookingDialog.applicationId}
        mentorName={bookingDialog.mentorName}
        courseTitle={bookingDialog.courseTitle}
      />
    </div>
  );
};

export default MentorApplications;
